import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import IncidentsList from './IncidentsList';
import CreateIncidentModal from './CreateIncidentModal';
import IncidentFilters from './IncidentFilters';
import IncidentStats from './IncidentStats';
import UnlinkedGarmentsList from './UnlinkedGarmentsList';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus } = FiIcons;

const IncidentsTab = () => {
  const [activeView, setActiveView] = useState('incidents'); // 'incidents' | 'unlinked'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({ status: '', priority: '', category: '', search: '' });
  
  const { incidents, inventoryData, currentProject } = useData();

  // Contador de notificaciones (Tipología 9 + Reglas de Rol)
  const unlinkedCount = useMemo(() => {
    let count = 0;
    const isManager = currentProject?.role === 'manager';

    inventoryData.forEach(emp => {
      if (emp.garments) {
        count += emp.garments.filter(g => {
          const meta = g.metadata || {};
          const status = meta.GarmentStatus || '';
          const barcode = String(g.barcode || '');
          const unlinkedStatus = meta.unlinked_status;

          // Filtro base: Unlinked + Inicia por 9
          const isTarget = status.toLowerCase().includes('unlinked') && barcode.startsWith('9');
          if (!isTarget) return false;

          // Filtro de Rol
          if (unlinkedStatus === 'sent') return false; 
          if (unlinkedStatus === 'withdrawn' && !isManager) return false; 

          return true;
        }).length;
      }
    });
    return count;
  }, [inventoryData, currentProject]);

  const filteredIncidents = incidents.filter(incident => {
    const matchesStatus = !filters.status || incident.status === filters.status;
    const matchesPriority = !filters.priority || incident.priority === filters.priority;
    const matchesCategory = !filters.category || incident.category === filters.category;
    const matchesSearch = !filters.search || Object.values(incident)
      .some(value => value && value.toString().toLowerCase().includes(filters.search.toLowerCase()));
    return matchesStatus && matchesPriority && matchesCategory && matchesSearch;
  });

  return (
    <div className="space-y-4">
      {/* PESTAÑAS TOGGLE (ESTILO LIMPIO SIN ICONOS) */}
      <div className="bg-gray-100 p-1 rounded-xl flex items-center relative mb-6">
        <button
          onClick={() => setActiveView('incidents')}
          className={`flex-1 flex items-center justify-center py-2 rounded-lg text-sm font-bold transition-all relative z-10 ${
            activeView === 'incidents' 
              ? 'bg-white text-gray-800 shadow-sm' 
              : 'text-gray-500 hover:text-gray-600'
          }`}
        >
          Incidencias
        </button>

        <button
          onClick={() => setActiveView('unlinked')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all relative z-10 ${
            activeView === 'unlinked' 
              ? 'bg-white text-red-600 shadow-sm' 
              : 'text-gray-500 hover:text-gray-600'
          }`}
        >
          Unlinked
          {unlinkedCount > 0 && (
            <span className="bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full ml-1 font-black min-w-[1.2rem] text-center">
              {unlinkedCount}
            </span>
          )}
        </button>
      </div>

      {/* VISTA 1: INCIDENCIAS */}
      {activeView === 'incidents' && (
        <motion.div 
          initial={{ opacity: 0, y: 5 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="space-y-4"
        >
          <div className="flex items-center justify-between px-1">
            {/* Título Normalizado */}
            <h2 className="text-lg font-medium text-gray-800 tracking-tight">Registro de Incidencias</h2>
            
            {/* Botón Compacto */}
            <motion.button 
              whileTap={{ scale: 0.95 }} 
              onClick={() => setShowCreateModal(true)} 
              className="flex items-center justify-center space-x-2 px-3 py-1.5 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold transition-colors text-[10px] shadow-sm uppercase tracking-wider"
            >
              <SafeIcon icon={FiPlus} />
              <span>NUEVA</span>
            </motion.button>
          </div>

          <IncidentStats incidents={incidents} />
          <IncidentFilters filters={filters} onFiltersChange={setFilters} />
          <IncidentsList incidents={filteredIncidents} />
        </motion.div>
      )}

      {/* VISTA 2: PRENDAS DESVINCULADAS (UNLINKED) */}
      {activeView === 'unlinked' && (
        <motion.div 
          initial={{ opacity: 0, y: 5 }} 
          animate={{ opacity: 1, y: 0 }}
        >
           <div className="mb-4 px-1">
              <h2 className="text-lg font-medium text-gray-800 tracking-tight">Gestión de Retiradas (Tipo 9)</h2>
              <p className="text-xs text-gray-400 mt-1 font-medium">
                Retirada física de prendas marcadas como desvinculadas.
              </p>
           </div>
           <UnlinkedGarmentsList />
        </motion.div>
      )}

      {showCreateModal && <CreateIncidentModal onClose={() => setShowCreateModal(false)} />}
    </div>
  );
};

export default IncidentsTab;